import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  IconButton,
  Tooltip,
  Stack,
  Typography,
} from "@mui/material";
import {
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { tokens } from "../../theme";
import Header from "../../InfinitePages/Dashboard/Header";
import Swal from "sweetalert2";

const PREFERENCE_KEY = "infiniteAppUserPreferences";

export default function InfiniteSetting() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const [preferences, setPreferences] = useState({
    rowHeight: "",
    fontFamily: "Source Sans Pro",
    fontSize: 16,
    tableAlign: "center",
  });

  useEffect(() => {
    const saved = localStorage.getItem(PREFERENCE_KEY);
    if (saved) {
      setPreferences(JSON.parse(saved));
    }
  }, []);

  const handleChange = (key) => (event) => {
    setPreferences({ ...preferences, [key]: event.target.value });
  };

  const handleSave = () => {
    localStorage.setItem(PREFERENCE_KEY, JSON.stringify(preferences));
    Swal.fire({
      title: "Enabled!",
      text: "preferences Settings Save Successfully.",
      icon: "success",
      confirmButtonText: "OK",
      confirmButtonColor: "#28a745",
      width: "300px",
      padding: "1em",
      customClass: {
        popup: "small-alert",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        window.location.reload();
      }
    });
    navigate(-1);
  };

  const fonts = [
    '"Times New Roman"',
    "Arial",
    "Calibri",
    "Verdana",
    '"Source Sans Pro"',
    "sans-serif",
  ];

  const fontSizes = [10, 11, 12, 14, 16, 18, 20];

  return (
    <Box px={2} pt={10}>
      <Header
        title="Settings"
        subtitle="Customize your preferences Settings."
      />

      <Box display="flex" justifyContent="center" alignItems="flex-start">
        <Paper
          elevation={3}
          sx={{
            width: { xs: "300px", sm: "400px", md: "500px" },
            p: 3,
            borderRadius: "10px",
            mt: 2,
            backgroundColor: colors.primary[400],
          }}
        >
          {/* Toolbar */}
          <Stack direction="column" spacing={3} mb={2}>
            <Stack direction="row" spacing={3} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Font</InputLabel>
                <Select
                  value={preferences.fontFamily}
                  label="Font"
                  onChange={handleChange("fontFamily")}
                >
                  {fonts.map((font) => (
                    <MenuItem key={font} value={font}>
                      {font}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Size</InputLabel>
                <Select
                  value={preferences.fontSize}
                  label="Size"
                  onChange={handleChange("fontSize")}
                >
                  {fontSizes.map((size) => (
                    <MenuItem key={size} value={size}>
                      {size}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <Typography>Text Alignment</Typography>
            <Stack
              direction="row"
              spacing={4}
              alignItems="center"
              flexWrap="wrap"
            >
              {[
                {
                  label: "Align Left",
                  value: "left",
                  icon: <FormatAlignLeft />,
                },
                {
                  label: "Center",
                  value: "center",
                  icon: <FormatAlignCenter />,
                },
                {
                  label: "Align Right",
                  value: "right",
                  icon: <FormatAlignRight />,
                },
                {
                  label: "Justify",
                  value: "justify",
                  icon: <FormatAlignJustify />,
                },
              ].map(({ label, value, icon }) => (
                <Tooltip key={value} title={label}>
                  <IconButton
                    onClick={() =>
                      setPreferences({ ...preferences, tableAlign: value })
                    }
                    sx={{
                      backgroundColor:
                        preferences.tableAlign === value
                          ? "#d3d3d3"
                          : "transparent",
                      borderRadius: "50%",
                    }}
                  >
                    {icon}
                  </IconButton>
                </Tooltip>
              ))}
            </Stack>
          </Stack>

          <Grid container spacing={2} mt={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Table Row Height</InputLabel>
                <Select
                  value={preferences.rowHeight}
                  label="Table Row Height"
                  onChange={handleChange("rowHeight")}
                >
                  <MenuItem value="30">Small</MenuItem>
                  <MenuItem value="40">Medium</MenuItem>
                  <MenuItem value="50">Large</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Box display="flex" alignItems="center" justifyContent="center">
            <Button
              variant="contained"
              sx={{
                mt: 3,
                width: "40%",
                backgroundColor: "#B09246",
                ":hover": { backgroundColor: "#B09246" },
                borderRadius: "10px",
              }}
              onClick={handleSave}
              disabled={!preferences.rowHeight}
            >
              Save
            </Button>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
